// Sending a signed order out of the clinic.
//
// Two destinations, and they behave very differently — which is the whole
// reason this screen exists rather than a bare "send" button. A fax goes
// through SRFax and lands back in Admin ▸ Fax Inbox ▸ Sent; an email goes out
// of the shared MagicNotes Zoho mailbox, so replies do NOT come back to the
// doctor who pressed the button. Both hand a patient's name, DOB, MRN and
// orders to somebody outside the clinic, so the destination is stated in
// plain words and confirmed before anything leaves.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  listFaxContacts, listFaxSenderEmails, sendOrderFax, emailOrderDoc,
  getCurrentUser,
} from "../../services/orderPaperwork";

// Mirrors the backend's `_ADDRESS`. Checked here too so a typo costs a
// keystroke rather than a round trip.
const ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const SENDER_MAILBOX = "social_media@magicnotesai.com";

function splitAddresses(raw) {
  return String(raw || "")
    .split(/[,;]/)
    .map((a) => a.trim())
    .filter(Boolean);
}

export default function SendSignedOrderModal({
  patientId, patientName, schema, doc, mode,
  signerName = "",
  onClose, onSent,
}) {
  const isFax = mode === "fax";
  const [me, setMe] = useState(null);

  const [faxNumber, setFaxNumber] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderEmails, setSenderEmails] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(
    patientName ? `Order for ${patientName}` : "Order"
  );
  const [message, setMessage] = useState("");

  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser().then((u) => { if (!cancelled) setMe(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isFax) return undefined;
    let cancelled = false;
    listFaxContacts({ userId: me?.id, schema })
      .then((r) => { if (!cancelled) setContacts(r?.contacts || []); })
      .catch(() => {});
    listFaxSenderEmails()
      .then((list) => {
        if (cancelled) return;
        setSenderEmails(list);
        setSenderEmail((v) => v || list[0] || "");
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isFax, me, schema]);

  const addresses = useMemo(() => splitAddresses(to), [to]);
  const ready = isFax ? faxNumber.trim().length > 0 : addresses.length > 0;

  // Anything typed invalidates a confirmation already on screen: confirming a
  // number and then editing it must not send to the edited one unreviewed.
  const edit = (setter) => (value) => {
    setConfirming(false);
    setError(null);
    setter(value);
  };

  const review = () => {
    setError(null);
    if (!isFax) {
      const bad = addresses.find((a) => !ADDRESS.test(a));
      if (bad) {
        setError(`“${bad}” is not an email address.`);
        return;
      }
    }
    setConfirming(true);
  };

  const send = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      if (isFax) {
        const res = await sendOrderFax({
          path: doc.path,
          faxNumber: faxNumber.trim(),
          recipientName: recipientName.trim(),
          senderName: signerName,
          senderEmail,
          subject: subject.trim(),
          sentBy: signerName || null,
          sentByUserId: me?.id || null,
          schema,
        });
        setSent({ where: faxNumber.trim(), queueId: res?.queue_id || null });
      } else {
        const res = await emailOrderDoc({
          patientId, path: doc.path, to: to.trim(),
          subject: subject.trim(), message: message.trim(),
        });
        setSent({ where: (res?.sent_to || addresses).join(", ") });
      }
      setConfirming(false);
      onSent?.();
    } catch (err) {
      setError(err?.message || String(err));
      setConfirming(false);
    } finally {
      setSending(false);
    }
  }, [isFax, doc.path, faxNumber, recipientName, signerName, senderEmail,
      subject, me, schema, patientId, to, message, addresses, onSent]);

  if (sent) {
    return (
      <Shell title={isFax ? "Fax sent" : "Email sent"} onClose={onClose}>
        <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-500/[0.08] px-3 py-3 text-sm">
          <div className="font-semibold text-emerald-800 dark:text-emerald-200">
            Sent to {sent.where}.
          </div>
          <div className="mt-1 text-emerald-800/80 dark:text-emerald-200/80">
            {isFax ? (
              <>
                {sent.queueId ? `SRFax queue ID ${sent.queueId}. ` : ""}
                Delivery is not instant — SRFax retries busy lines. The result
                shows up under <strong>Admin Page ▸ Fax Inbox ▸ Sent</strong>.
              </>
            ) : (
              <>
                Replies go to {SENDER_MAILBOX}, not to your own inbox.
              </>
            )}
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-700 text-white"
          >
            Done
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title={isFax ? "Send this order as a fax" : "Send this order by email"}
      onClose={onClose}
    >
      {error && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-500/[0.08] px-3 py-2 text-sm font-semibold text-rose-800 dark:text-rose-200"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-[1fr_300px] gap-4">
        <div className="space-y-3">
          {isFax ? (
            <>
              <Field label="Recipient fax number" id="fax-number">
                <input
                  id="fax-number"
                  value={faxNumber}
                  onChange={(e) => edit(setFaxNumber)(e.target.value)}
                  placeholder="813-992-4598"
                  className={inputCls}
                />
              </Field>
              <Field label="Recipient name" id="fax-recipient" optional>
                <input
                  id="fax-recipient"
                  value={recipientName}
                  onChange={(e) => edit(setRecipientName)(e.target.value)}
                  className={inputCls}
                />
              </Field>
              {senderEmails.length > 0 && (
                <Field label="Confirmation goes to" id="fax-sender">
                  <select
                    id="fax-sender"
                    value={senderEmail}
                    onChange={(e) => edit(setSenderEmail)(e.target.value)}
                    className={inputCls}
                  >
                    {senderEmails.map((e) => (
                      <option key={e} value={e}>{e}</option>
                    ))}
                  </select>
                </Field>
              )}
              {contacts.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold mb-1 text-slate-500 dark:text-slate-400">
                    Saved contacts
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {contacts.map((c) => (
                      <button
                        key={c.id || c.fax_number}
                        type="button"
                        onClick={() => {
                          setConfirming(false);
                          setFaxNumber(c.fax_number || "");
                          setRecipientName(c.name || "");
                        }}
                        className="text-[11px] px-2 py-1 rounded-full ring-1 ring-slate-300 dark:ring-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                      >
                        {c.name || c.fax_number}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="Send to" id="email-to">
                <input
                  id="email-to"
                  value={to}
                  onChange={(e) => edit(setTo)(e.target.value)}
                  placeholder="lab@example.com, office@example.com"
                  className={inputCls}
                />
              </Field>
              <Field label="Subject" id="email-subject">
                <input
                  id="email-subject"
                  value={subject}
                  onChange={(e) => edit(setSubject)(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="Note" id="email-message" optional>
                <textarea
                  id="email-message"
                  rows={3}
                  value={message}
                  onChange={(e) => edit(setMessage)(e.target.value)}
                  className={inputCls}
                />
              </Field>
            </>
          )}
        </div>

        {/* The walk-through. Where this goes, by what route, and where to look
            for it afterwards — stated before the send, not after. */}
        <div
          data-testid="destination"
          className="rounded-lg bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200 dark:ring-slate-700 p-3 text-xs leading-relaxed text-slate-700 dark:text-slate-200"
        >
          <div className="font-semibold mb-2">Where this is going</div>
          <p className="mb-2">
            <span className="font-mono break-all">{doc.filename}</span>
            {patientName ? <> — the signed order for <strong>{patientName}</strong>.</> : "."}
          </p>
          {isFax ? (
            <>
              <p className="mb-2">
                It is uploaded to <strong>SRFax</strong>, the clinic&apos;s fax
                carrier, which dials{" "}
                <strong>{faxNumber.trim() || "the number you enter"}</strong>
                {recipientName.trim() ? <> at {recipientName.trim()}</> : null}.
              </p>
              {senderEmail && (
                <p className="mb-2">
                  SRFax emails the delivery confirmation to{" "}
                  <strong>{senderEmail}</strong>.
                </p>
              )}
              <p>
                A record is filed under the <strong>{schema}</strong> clinic and
                shows up in <strong>Admin Page ▸ Fax Inbox ▸ Sent</strong>,
                alongside faxes sent from there.
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">
                The PDF is <strong>attached</strong> to an email sent over Zoho
                from <strong>{SENDER_MAILBOX}</strong> — the shared MagicNotes
                mailbox, not your own address.
              </p>
              <p className="mb-2">
                It is delivered to{" "}
                <strong>{addresses.length ? addresses.join(", ") : "the address you enter"}</strong>.
              </p>
              <p>
                Anyone who replies reaches {SENDER_MAILBOX}. There is no inbox
                for it in the app, so ask for a fax or a phone call back if you
                need an answer.
              </p>
            </>
          )}
        </div>
      </div>

      {confirming && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          <strong>About to send</strong> {doc.filename} to{" "}
          <strong>{isFax ? faxNumber.trim() : addresses.join(", ")}</strong>.
          This leaves the clinic and cannot be recalled.
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-3 py-1.5 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600"
        >
          Cancel
        </button>
        {confirming ? (
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-rose-500 to-red-600 text-white shadow-sm disabled:opacity-50"
          >
            {sending ? "Sending…" : "Yes, send it"}
          </button>
        ) : (
          <button
            type="button"
            onClick={review}
            disabled={!ready || sending}
            className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFax ? "Send fax" : "Send email"}
          </button>
        )}
      </div>
    </Shell>
  );
}

const inputCls =
  "w-full text-sm px-2 py-1.5 rounded-md ring-1 ring-slate-300 dark:ring-slate-600 " +
  "bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100";

function Field({ label, id, optional, children }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] font-semibold mb-1 text-slate-600 dark:text-slate-300"
      >
        {label}
        {optional && <span className="font-normal text-slate-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

// Rendered INLINE, never portalled to document.body: Tailwind here is scoped
// to #clinic-root, and a portal lands outside it and paints invisible.
function Shell({ title, onClose, children }) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-900/40 p-6">
      <div
        role="dialog"
        aria-label={title}
        className="w-[720px] max-w-full rounded-xl bg-white dark:bg-slate-900 shadow-xl ring-1 ring-slate-200 dark:ring-slate-700 p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="ml-auto text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
